import React, { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { trackPageView } from './lib/gtm';
import './App.css';
import { LandingPage } from './components/LandingPage';
import { LoginPage } from './components/auth/LoginPage';
import { ForgotPassword } from './components/auth/ForgotPassword';
import { ResetPassword } from './components/auth/ResetPassword';
import { AccountBlockedPage } from './components/auth/AccountBlockedPage';
import { SafetyPage } from './components/unused/SafetyPage';
import { GuideMatch } from './components/match/GuideMatch';
import { FindingMatch } from './components/match/FindingMatch';
import { HomePage } from './components/HomePage';
import { HomeDashboard } from './components/HomeDashboard';
import { HomeChatView } from './components/communication/HomeChatView';
import { MeetingWaitingRoom } from './components/communication/MeetingWaitingRoom';
import { MeetingView } from './components/communication/MeetingView';
import { SeekerProfilePage } from './components/profiles/SeekerProfilePage';
import { EditProfilePage } from './components/profiles/EditProfilePage';
import { NotificationSettingsPage } from './components/profiles/NotificationSettingsPage';
import { PaymentMethodsPage } from './components/billing/PaymentMethodsPage';
import { PackageCheckoutPage } from './components/packages/PackageCheckoutPage';
import { CheckoutSuccessPage } from './components/booking/CheckoutSuccessPage';
import { SeekerOnboardingRoute } from './routes/SeekerOnboardingRoute';
import { onboardingSections } from './onboarding/sections';
import { OnboardingAnswers } from './types/onboarding';
import { MatchProvider } from './state/MatchContext';
import { SeekerOnboardingMatchRoutes } from './routes/SeekerOnboardingMatchRoutes';
import { AuthCallback } from './components/auth/AuthCallback';
import { ConnectInviteLanding } from './components/invited/ConnectInviteLanding';
import { ConnectBookingPage } from './components/invited/ConnectBookingPage';
import { TransactionsPage } from './components/billing/TransactionsPage';
import RequestNewMatch from './components/match/RequestNewMatch';
import TermsOfServicePage from './components/tos/TermsOfServicePage';
import StaticTermsPage from './components/tos/StaticTermsPage';
import PrivacyPolicyPage from './components/privacy/PrivacyPolicyPage';
import { ReturningUserPage } from './components/onboarding/ReturningUserPage';
import { ContinueOnboarding } from './components/onboarding/ContinueOnboarding';
import { NotAFit } from './components/onboarding/NotAFit';
import { OffPlatformReviewPage } from './components/reviews/OffPlatformReviewPage';
import { SeekerOnboardingService } from './services/onboardingService';
import JoinSession from './components/JoinSession';
import { PostHogIdentify } from './components/PostHogIdentify';

const firstQuestionId = onboardingSections[0]?.questions[0]?.id ?? '';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function GTMPageTracker() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search)
  }, [location]);

  return null;
}

const ANSWERS_STORAGE_KEY = 'onboarding_answers';

function App() {
  const [answers, setAnswers] = useState<OnboardingAnswers>(() => {
    try {
      const raw = localStorage.getItem(ANSWERS_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as OnboardingAnswers) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(ANSWERS_STORAGE_KEY, JSON.stringify(answers));
    } catch {
      // storage quota exceeded — silent fail
    }
  }, [answers]);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <PostHogIdentify />
      <GTMPageTracker />
      <MatchProvider>
        <AppRoutes answers={answers} setAnswers={setAnswers} />
      </MatchProvider>
    </BrowserRouter>
  );
}

type AppRoutesProps = {
  answers: OnboardingAnswers;
  setAnswers: React.Dispatch<React.SetStateAction<OnboardingAnswers>>;
};

function ReturningUserPageWrapper() {
  const location = useLocation();
  const state = location.state as { email: string; onboardingStarted: boolean; accountExists: boolean; isGuide?: boolean } | null;

  // If no state, show a default message but don't redirect
  if (!state || !state.email) {
    return (
      <ReturningUserPage
        email=""
        onboardingStarted={false}
        accountExists={false}
        isGuide={false}
      />
    );
  }

  return (
    <ReturningUserPage
      email={state.email}
      onboardingStarted={state.onboardingStarted}
      accountExists={state.accountExists}
      isGuide={state.isGuide || false}
    />
  );
}

function AppRoutes({ answers, setAnswers }: AppRoutesProps) {
  const navigate = useNavigate();

  const startOnboarding = () => {
    if (!firstQuestionId) return;
    SeekerOnboardingService.clearOnboardingData();
    localStorage.removeItem(ANSWERS_STORAGE_KEY);
    setAnswers({});
    navigate('/onboarding/seeker_email');
  };

  const restartFlow = () => {
    localStorage.removeItem(ANSWERS_STORAGE_KEY);
    setAnswers({});
    navigate('/', { replace: true });
  };

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      {/* Redirect old safety-and-security route to landing page */}
      <Route path="/safety-and-security" element={<Navigate to="/" replace />} />
      <Route path="/connect/:identifier" element={<ConnectInviteLanding />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/forgot" element={<ForgotPassword />} />
      <Route path="/auth/reset" element={<ResetPassword />} />
      <Route path="/account-blocked" element={<AccountBlockedPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/connect/booking" element={<ConnectBookingPage />} />
      <Route
        path="/onboarding"
        element={<Navigate to="/onboarding/seeker_email" replace />}
      />
      {/* Redirect old intro route to first question */}
      <Route
        path="/onboarding/intro"
        element={<Navigate to="/onboarding/seeker_email" replace />}
      />
      <Route path="/onboarding/safety" element={<SafetyPage onRestart={restartFlow} />} />
      <Route path="/onboarding/returning-user" element={<ReturningUserPageWrapper />} />
      <Route path="/onboarding/continue" element={<ContinueOnboarding setAnswers={setAnswers} />} />
      <Route path="/onboarding/not-a-fit" element={<NotAFit />} />
      <Route
        path="/onboarding/:sectionId"
        element={<SeekerOnboardingRoute answers={answers} setAnswers={setAnswers} />}
      />
      <Route path="/onboarding/terms" element={<TermsOfServicePage />} />
      <Route path="/onboarding/finding-match" element={<FindingMatch />} />
      <Route path="/onboarding/match" element={<GuideMatch variant="match" />} />
      <Route path="/onboarding/match/*" element={<SeekerOnboardingMatchRoutes />} />
      <Route path="/onboarding/no-match" element={<GuideMatch variant="no_match" />} />
      <Route path="/home/*" element={<HomePage />}>
        <Route index element={<HomeDashboard />} />
        <Route path="chat" element={<HomeChatView />} />
        <Route path="waiting-room" element={<MeetingWaitingRoom />} />
        <Route path="meeting" element={<MeetingView />} />
        <Route path="profile" element={<SeekerProfilePage />} />
        <Route path="package-checkout" element={<PackageCheckoutPage />} />
        <Route path="transactions" element={<TransactionsPage />} />
        <Route path='request-new-match' element={<RequestNewMatch />} />
      </Route>
      <Route path="/profile" element={<SeekerProfilePage />} />
      <Route path="/profile/notifications" element={<NotificationSettingsPage />} />
      <Route path="/profile/edit" element={<EditProfilePage />} />
      <Route path="/payment-methods" element={<PaymentMethodsPage />} />
      <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/terms/full" element={<StaticTermsPage />} />
      <Route path="/privacypolicy" element={<PrivacyPolicyPage />} />
      <Route path="/off-platform-review" element={<OffPlatformReviewPage />} />
      <Route path="/join/:sessionId" element={<JoinSession />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
